import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { styled } from "@mui/material/styles";
import autobind from "autobind-decorator";
import { RouterStore } from "mobx-react-router";
import AccountService, { LoginResult } from "../../service/AccountService";
import { inject, observer } from "mobx-react";
import { observable, action } from "mobx";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import firebase from "firebase/compat/app";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Snackbar,
} from "@mui/material";
import { StyledAvatar } from "../common/StyledAvatar";

const Paper = styled("div")((props) => ({
  marginTop: props.theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const Form = styled("form")((props) => ({
  width: "100%", // Fix IE 11 issue.
  marginTop: props.theme.spacing(1),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
  minWidth: "120px",
  height: "38px",
}));

export const Text = styled("span")({
  margin: "20px 0px",
  fontSize: "14px",
  fontFamily: "Roboto",
  color: "#9E9E9E",
  alignSelf: "center",
  maxWidth: "300px",
  lineHeight: "20px",
  "@media screen and (max-height: 590px)": {
    margin: "10px 0px",
  },
});

type LoginPageProps = {
  routing?: RouterStore;
  account?: AccountService;
};

type LoginState = "NORMAL" | "LOADING" | LoginResult;

@inject("routing")
@inject("account")
@observer
export default class LoginPage extends React.Component<LoginPageProps> {
  @observable
  private _status: LoginState = "NORMAL";

  @observable
  private _email = "";

  @observable
  private _keepLogging = false;

  @observable
  private _password = "";

  @observable
  isForgotPassword = false;

  @observable
  fpUsername = "";

  @observable
  snackMessage = "";

  @autobind
  handleEmailChange(event) {
    this._email = event.target.value?.trim();
  }

  @autobind
  handlePasswordChange(event) {
    this._password = event.target.value;
  }

  @autobind
  handleKeepLogging(event: React.ChangeEvent<HTMLInputElement>): void {
    this._keepLogging = event.target.checked;
  }

  getEmailErrorMessage(): string {
    if (this._status == "INVALID_EMAIL") {
      return "Email is invalid.";
    }

    if (this._status == "NO_SUCH_USER") {
      return "Email is not registered.";
    }
    return null;
  }

  getPasswordErrorMessage(): string {
    if (this._status == "INVALID_PASSWORD") {
      return "Password is invalid.";
    }
    return null;
  }

  @autobind
  async handleSubmit(event): Promise<void> {
    event.preventDefault();
    this._status = "LOADING";
    var { account, routing } = this.props;

    if (this._keepLogging) {
      this._status = await account.handleLogin(this._email, this._password);
    } else {
      await firebase
        .auth()
        .setPersistence(firebase.auth.Auth.Persistence.SESSION);
      this._status = await account.handleLogin(this._email, this._password);
    }

    if (this._status == "SUCCESS") {
      routing.replace("/");
    }
  }

  @autobind
  handleCreateAccount() {
    this.props.routing.push("/signup");
  }

  // @autobind
  // handleAdminLogin(){
  // 	this.props.routing.push("/adminlogin")
  // }

  @autobind
  async resetPassword() {
    let result = await this.props.account.handlePasswordReset(this.fpUsername);

    if (result === "SUCCESS") {
      this.snackMessage = "Sent Password Reset Email!";
      this.isForgotPassword = false;
    } else {
      this.snackMessage = result.toString();
    }
  }

  render() {
    var emailError = this.getEmailErrorMessage();
    var passwordError = this.getPasswordErrorMessage();

    return (
      <Container component="main" maxWidth="xs">
        <Paper>
          <StyledAvatar img={require("../../assets/favicon.ico")} />
          <Typography component="h1" variant="h5">
            Log in
          </Typography>
          <Form noValidate onSubmit={this.handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              label="Email Address"
              autoComplete="email"
              autoFocus
              value={this._email}
              onChange={this.handleEmailChange}
              disabled={this._status == "LOADING"}
              error={emailError != null}
              helperText={emailError}
            />

            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              label="Password"
              type="password"
              autoComplete="current-password"
              value={this._password}
              onChange={this.handlePasswordChange}
              disabled={this._status == "LOADING"}
              error={passwordError != null}
              helperText={passwordError}
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={this._keepLogging}
                  onChange={this.handleKeepLogging}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label="Stay Logged In"
            />

            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Grid item>
                <SubmitButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={this._status == "LOADING"}
                >
                  {this._status == "LOADING" ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Typography>Log In</Typography>
                  )}
                </SubmitButton>
              </Grid>
              <Grid item>
                <SubmitButton
                  type="button"
                  variant="contained"
                  onClick={this.handleCreateAccount}
                  disabled={this._status == "LOADING"}
                  style={{ backgroundColor: "#e0e0e0", color: "#000" }}
                >
                  <Typography>Create Account</Typography>
                </SubmitButton>
              </Grid>
            </Grid>

            <div style={{ height: "12px" }} />

            <Grid container direction="row" justifyContent="center">
              {/* <Link variant="body2">
								Forgot password?
              				</Link> */}
              <Text>
                <span
                  onClick={() => (this.isForgotPassword = true)}
                  style={{
                    color: "#3f51b5",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  Forgot Password?
                </span>
              </Text>
            </Grid>

            {/* <div style={{ height: '12px' }} />
						
						<Grid container direction="row" justify="center">
							<SubmitButton type="button" variant="contained" color="secondary" onClick={this.handleAdminLogin}
								disabled={this._status == "LOADING"}>
								<Typography>ADMIN LOGIN</Typography>
                			</SubmitButton>
						</Grid> */}
          </Form>
          <Dialog
            open={this.isForgotPassword}
            onClose={() => {
              this.isForgotPassword = false;
            }}
            aria-labelledby="form-dialog-title"
          >
            <DialogTitle id="form-dialog-title">Reset Password</DialogTitle>
            <DialogContent>
              <DialogContentText>
                To reset password, please enter your email address and the new
                password here.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                label="Email Address"
                type="email"
                fullWidth
                value={this.fpUsername}
                onChange={(e) => (this.fpUsername = e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => (this.isForgotPassword = false)}
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={this.resetPassword} color="primary">
                Send Password Reset Email
              </Button>
            </DialogActions>
          </Dialog>
          <Snackbar
            open={this.snackMessage ? true : false}
            autoHideDuration={6000}
            onClose={() => (this.snackMessage = null)}
          >
            <div
              style={{
                backgroundColor: "orange",
                color: "white",
                padding: "10px",
                borderRadius: "5px",
              }}
            >
              {this.snackMessage}
            </div>
            {/* <MuiAlert elevation={6} variant="filled" severity="info" onClose={()=>this.snackMessage=null}>{this.snackMessage}</MuiAlert> */}
          </Snackbar>
        </Paper>
      </Container>
    );
  }
}
